import React, { useEffect, useRef, useState } from 'react';
import './BeforeAfterCarousel.css';
import { useLanguage } from './LanguageContext';

// ──────────────────────────────────────────────────────
//  DATA  –  Replace placeholders with real patient photos
//  Each entry needs:
//    label  : procedure name shown on the card
//    after  : path to the AFTER image  (shown by default)
//    before : path to the BEFORE image (shown on hover)
// ──────────────────────────────────────────────────────
const cases = [
  {
    label: { ES: 'Rinoplastia', EN: 'Rhinoplasty', PT: 'Rinoplastia' },
    after:  '/surgeriesfolder/rhinoplasty-after.webp',
    before: '/surgeriesfolder/rhinoplasty-before.webp',
  },
  {
    label: { ES: 'Lipoescultura', EN: 'Liposuction', PT: 'Lipoescultura' },
    after:  '/surgeriesfolder/lipoescultura-after.webp',
    before: '/surgeriesfolder/lipoescultura-before.webp',
  },
  {
    label: { ES: 'Blefaroplastia', EN: 'Blepharoplasty', PT: 'Blefaroplastia' },
    after:  '/surgeriesfolder/blefaroplastia-after.webp',
    before: '/surgeriesfolder/blefaroplastia-before.webp',
  },
  {
    label: { ES: 'Lifting Facial', EN: 'Face Lifting', PT: 'Lifting Facial' },
    after:  '/surgeriesfolder/lifting-facial-transformaciones-after.webp',
    before: '/surgeriesfolder/lifting-facial-transformaciones-before.webp',
  },
  {
    label: { ES: 'Lipomarcación', EN: 'HD Liposuction', PT: 'Lipo HD' },
    after:  '/surgeriesfolder/lipoescultura-after-alt.webp',
    before: '/surgeriesfolder/lipomarcacion-before.webp',
  },
  {
    label: { ES: 'Lipopapada', EN: 'Chin Liposuction', PT: 'Lipo de Papada' },
    after:  '/surgeriesfolder/lipopapada-after.webp',
    before: '/surgeriesfolder/lipopapada-before.webp',
  },
];

const VISIBLE = 4; // cards visible at once on desktop

const titles = {
  ES: 'TRANSFORMACIONES',
  EN: 'TRANSFORMATIONS',
  PT: 'TRANSFORMAÇÕES',
};

const swipeLabels = {
  ES: 'DESLIZA PARA VER MÁS',
  EN: 'SWIPE TO SEE MORE',
  PT: 'DESLIZE PARA VER MAIS',
};

const hoverHints = {
  ES: { before: 'ANTES', after: 'DESPUÉS' },
  EN: { before: 'BEFORE', after: 'AFTER' },
  PT: { before: 'ANTES', after: 'DEPOIS' },
};

const MOBILE_VISIBLE = 1;

export default function BeforeAfterCarousel() {
  const [startIndex, setStartIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [toggledCards, setToggledCards] = useState(() => new Set());
  const [isMobileView, setIsMobileView] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );
  const trackRef = useRef(null);
  const { language } = useLanguage();
  const visibleCount = isMobileView ? MOBILE_VISIBLE : VISIBLE;

  const total = cases.length;
  const maxStart = Math.max(total - visibleCount, 0);

  const prev = () => setStartIndex((i) => Math.max(i - 1, 0));
  const next = () => setStartIndex((i) => Math.min(i + 1, maxStart));

  useEffect(() => {
    function handleResize() {
      setIsMobileView(window.innerWidth <= 768);
    }

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isMobileView) {
      setToggledCards(new Set());
    }
  }, [isMobileView]);

  useEffect(() => {
    setStartIndex((currentIndex) => Math.min(currentIndex, maxStart));
  }, [maxStart]);

  const toggleCardImage = (index) => {
    if (!isMobileView) {
      return;
    }

    setToggledCards((current) => {
      const nextState = new Set(current);

      if (nextState.has(index)) {
        nextState.delete(index);
      } else {
        nextState.add(index);
      }

      return nextState;
    });
  };

  const visible = cases.slice(startIndex, startIndex + visibleCount);
  const hint = hoverHints[language] || hoverHints.EN;

  return (
    <section className="bac-section">
      <h2 className="bac-title">{titles[language]}</h2>

      <div className="bac-carousel-wrapper">
        {/* Left arrow */}
        <button
          className="bac-arrow bac-arrow--left"
          onClick={prev}
          disabled={startIndex === 0}
          aria-label="Previous"
        >
          ←
        </button>

        {/* Cards track */}
        <div className="bac-track" ref={trackRef}>
          {visible.map((item, i) => {
            const globalIdx = startIndex + i;
            const isShowingBefore = isMobileView
              ? toggledCards.has(globalIdx)
              : hoveredIndex === globalIdx;

            return (
              <div
                key={globalIdx}
                className="bac-card"
                onMouseEnter={() => setHoveredIndex(globalIdx)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={() => toggleCardImage(globalIdx)}
              >
                {/* AFTER image  — shown by default */}
                <img
                  src={item.after}
                  alt={`After – ${item.label[language]}`}
                  className={`bac-img bac-img--after ${isShowingBefore ? 'bac-img--hidden' : ''}`}
                  loading="lazy"
                />

                {/* BEFORE image — revealed on hover */}
                <img
                  src={item.before}
                  alt={`Before – ${item.label[language]}`}
                  className={`bac-img bac-img--before ${isShowingBefore ? 'bac-img--visible' : ''}`}
                  loading="lazy"
                />

                {/* Badge */}
                <span className={`bac-badge ${isShowingBefore ? 'bac-badge--before' : 'bac-badge--after'}`}>
                  {isShowingBefore ? hint.before : hint.after}
                </span>

                {/* Procedure label */}
                <div className="bac-label">{item.label[language]}</div>
              </div>
            );
          })}
        </div>

        {/* Right arrow */}
        <button
          className="bac-arrow bac-arrow--right"
          onClick={next}
          disabled={startIndex >= maxStart}
          aria-label="Next"
        >
          →
        </button>
      </div>

      {/* Dot indicators */}
      <div className="bac-dots">
        {Array.from({ length: maxStart + 1 }).map((_, i) => (
          <button
            key={i}
            className={`bac-dot ${i === startIndex ? 'bac-dot--active' : ''}`}
            onClick={() => setStartIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      <p className="bac-swipe-hint">{swipeLabels[language]}</p>
    </section>
  );
}
